// "Meu cadastro" (`/me/person`; Parte 6.2/6.5/6.7; REQ-PPL-007, 012, 015, 016; GIY-067): a PRÓPRIA pessoa vê o seu registro e edita o
// que lhe cabe — contatos, disponibilidade por vínculo flexível e ausências —, em modo `SelfScope` (a pessoa vem da claim `pid`
// do token, nunca da rota; sem pessoa → `404`, que a tela mostra como "sem cadastro", sem erro técnico). Um cartão por vínculo
// (pessoa jurídica), as certificações em leitura com o semáforo de validade. Nome, documento e nascimento só a coordenação
// altera (a tela diz isso); o identificador fiscal aparece mascarado. Sem escopo de rede ou tenant: serve a qualquer principal.
import { GiyusApiError } from '@giyus/api-client';
import { Alert, AlertDescription, AlertTitle, Skeleton } from '@giyus/ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ProblemAlert } from '../../../components';
import { useMe } from '../../../lib/auth/authContext';
import { AccountMeNav } from '../../../pages/Account';
import { Row } from '../../authz/components/Fields';
import { type PersonLink, useMyLinks, useMyPerson } from '../api/queries';
import type { PeopleScope } from '../api/scope';
import { MyAbsencesCard } from './MyAbsencesCard';
import { MyCertificationsCard } from './MyCertificationsCard';
import { MyContactsCard } from './MyContactsCard';
import { MyLinkCard } from './MyLinkCard';

/** `404` de "Meu cadastro": o usuário ainda não tem pessoa (convite sem cadastro, conta de serviço…). */
function hasNoRecord(error: unknown): boolean {
  return error instanceof GiyusApiError && error.status === 404;
}

/** Vigentes primeiro, depois os demais pela data de início mais recente. */
function sortLinks(links: readonly PersonLink[]): PersonLink[] {
  return [...links].sort((a, b) => {
    const activeA = a.status === 'Active' ? 0 : 1;
    const activeB = b.status === 'Active' ? 0 : 1;
    return activeA - activeB || b.effectiveFrom.localeCompare(a.effectiveFrom);
  });
}

export function MyRegistrationPage() {
  const { t } = useTranslation('people');
  const me = useMe();
  const person = useMyPerson();
  const links = useMyLinks();
  // O catálogo de tipos de certificação é referência de rede, lida pelo espelho do tenant (`/tenants/{t}/people/*`) por quem
  // não tem papel de rede; sem nenhum dos dois a tela mostra o código do tipo.
  const catalogScope = useMemo<PeopleScope | null>(() => {
    if (me.networkAccess.hasAccess) {
      return { kind: 'network' };
    }
    const first = me.contexts[0];
    return first ? { kind: 'tenant', tenantId: first.tenantId, tenantCode: first.code } : null;
  }, [me.networkAccess.hasAccess, me.contexts]);

  if (person.isError) {
    if (hasNoRecord(person.error)) {
      return (
        <section
          className="mx-auto flex max-w-3xl flex-col gap-4"
          data-testid="my-registration-empty"
        >
          <h1 className="text-headline font-semibold tracking-tight">{t('me.title')}</h1>
          <AccountMeNav />
          <Alert variant="default" role="status">
            <AlertTitle>{t('me.noRecord.title')}</AlertTitle>
            <AlertDescription>{t('me.noRecord.description')}</AlertDescription>
          </Alert>
        </section>
      );
    }
    return (
      <section className="mx-auto flex max-w-3xl flex-col gap-4">
        <AccountMeNav />
        <ProblemAlert
          error={person.error}
          onRetry={() => {
            void person.refetch();
          }}
        />
      </section>
    );
  }
  if (person.isLoading || !person.data) {
    return (
      <section className="mx-auto flex max-w-3xl flex-col gap-4">
        <AccountMeNav />
        <Skeleton className="mx-auto h-64 w-full max-w-3xl" />
      </section>
    );
  }
  const detail = person.data.data;

  return (
    <section className="mx-auto flex max-w-3xl flex-col gap-6" data-testid="my-registration">
      <div>
        <h1 className="text-headline font-semibold tracking-tight">{t('me.title')}</h1>
        <p className="text-muted-foreground">{t('me.subtitle')}</p>
      </div>
      <AccountMeNav />

      <section
        className="flex flex-col gap-3 rounded-card border p-4"
        aria-labelledby="my-identity-title"
        data-testid="my-identity"
      >
        <h2 id="my-identity-title" className="text-title font-semibold">
          {t('me.identity.title')}
        </h2>
        <dl className="flex flex-col gap-1">
          <Row label={t('me.identity.name')} value={detail.displayName} />
          {detail.socialName && (
            <Row label={t('me.identity.socialName')} value={detail.socialName} />
          )}
          <Row label={t('me.identity.country')} value={detail.country} />
          <Row
            label={t('me.identity.federalCode')}
            value={detail.federalCodeMasked ?? t('me.identity.federalCodePending')}
          />
        </dl>
        {detail.isPending && (
          <p className="text-body" role="status" data-testid="my-identity-pending">
            {t('me.identity.pending')}
          </p>
        )}
        <p className="text-caption text-muted-foreground">{t('me.identity.readOnly')}</p>
      </section>

      <MyContactsCard person={detail} />

      <section className="flex flex-col gap-3" aria-labelledby="my-links-title">
        <div>
          <h2 id="my-links-title" className="text-title font-semibold">
            {t('me.links.title')}
          </h2>
          <p className="text-body text-muted-foreground">{t('me.links.description')}</p>
        </div>
        {links.isError ? (
          <ProblemAlert
            error={links.error}
            onRetry={() => {
              void links.refetch();
            }}
          />
        ) : links.isLoading || !links.data ? (
          <Skeleton className="h-32 w-full" />
        ) : links.data.length === 0 ? (
          <p className="text-body text-muted-foreground" data-testid="my-links-empty">
            {t('me.links.empty')}
          </p>
        ) : (
          sortLinks(links.data).map((link) => <MyLinkCard key={link.id} link={link} />)
        )}
      </section>

      <MyAbsencesCard />
      <MyCertificationsCard catalogScope={catalogScope} />
    </section>
  );
}
